import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Typography,
    Stack,
    CircularProgress,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
} from '@mui/material';
import { Close, Check } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { Paper } from '@shared/ui/common/paper';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useConnections } from '@shared/hooks';
import { useImpersonation } from '@shared/hooks/useImpersonation';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { useProductFlowMarketplaces, useProductFlowSettings, useProductFlowSync } from '../hooks/useProductFlow';
import ConnectionActiveImports from '../../generic/components/ConnectionActiveImports';
import { ACTIVE_IMPORT_JOBS_QUERY_KEY } from '../../hooks/useActiveImportJobs';
import { APP_CONFIG } from '../../../../config/app.config';
import { ProductMatchField } from '@domain/connection/models/ConnectionSettings';
import { useToastStore } from '../../../../application/store/toastStore';
import { useImportJobsStore } from '../../../../application/store/importJobsStore';

const ALL_MARKETPLACES = '__all__';

interface ProductFlowConnectionSettingsProps {
    connectionId: string;
}

const ProductFlowConnectionSettings = ({ connectionId }: ProductFlowConnectionSettingsProps) => {
    const { onClose } = useGenericDrawer();
    const queryClient = useQueryClient();

    const { connections, removeConnection } = useConnections();
    const connection = connections?.find(c => c.id === connectionId);

    const { canImpersonate } = useImpersonation();


    const { settingsResponse, isLoading, updateSettings, isUpdating } = useProductFlowSettings(connectionId);
    const { marketplaces, isLoading: isLoadingMarketplaces } = useProductFlowMarketplaces(connectionId);
    const { syncAllOrders, syncNewOrders, syncProducts, syncInventory, syncMarketplaceOffers } = useProductFlowSync();
    const addJob = useImportJobsStore((s) => s.addJob);

    const [matchField, setMatchField] = useState<ProductMatchField>('ean');
    /** Empty array means all marketplaces (same contract as backend). */
    const [selectedMarketplaces, setSelectedMarketplaces] = useState<string[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [activeSync, setActiveSync] = useState<string | null>(null);
    const addToast = useToastStore(state => state.addToast);

    useEffect(() => {
        if (settingsResponse) {
            setSelectedMarketplaces(settingsResponse.import?.allowedMarketplaces ?? []);
            setMatchField(settingsResponse.import?.productMatchField ?? 'ean');
        }
    }, [settingsResponse]);

    const allMarketplacesSelected = selectedMarketplaces.length === 0;

    const availableMarketplaces: { machine_name: string, name: string }[] = useMemo(() => {
        const machineNames = new Set<string>();

        // Show saved allowlist immediately so chips render before ProductFlow finishes loading
        for (const name of settingsResponse?.import?.allowedMarketplaces ?? []) {
            if (name) machineNames.add(name);
        }

        for (const marketplace of marketplaces) {
            if (marketplace.machine_name) machineNames.add(marketplace.machine_name);
        }

        const availableMarketplaces = [...machineNames].map(machineName => {
            const marketplace = marketplaces.find(m => m.machine_name === machineName);
            return marketplace ? marketplace : { machine_name: machineName, name: machineName };
        })


        return availableMarketplaces.sort((a, b) => a.name.localeCompare(b.name));
    }, [marketplaces, settingsResponse]);

    const handleToggle = (marketplaceMachineName: string) => {
        if (marketplaceMachineName === ALL_MARKETPLACES) {
            setSelectedMarketplaces([]);
            return;
        }

        setSelectedMarketplaces(prev => {
            if (prev.length === 0) {
                return [marketplaceMachineName];
            }
            if (prev.includes(marketplaceMachineName)) {
                return prev.filter(m => m !== marketplaceMachineName);
            }
            return [...prev, marketplaceMachineName];
        });
    };

    const savedImport = settingsResponse?.import;
    const savedAllowed = savedImport?.allowedMarketplaces ?? [];
    const isDirty = !!settingsResponse && (
        JSON.stringify([...selectedMarketplaces].sort()) !== JSON.stringify([...savedAllowed].sort()) ||
        matchField !== (savedImport?.productMatchField ?? 'ean')
    );

    const handleSave = async () => {
        try {
            await updateSettings({
                import: {
                    allowedMarketplaces: selectedMarketplaces,
                    productMatchField: matchField,
                },
            });
            addToast('Settings saved', 'success');
        } catch {
            addToast('Failed to save settings', 'error');
        }
    };

    const syncActions: { key: string; label: string; run: (connectionId: string) => Promise<{ jobId: string }> }[] = [
        { key: 'orders-new', label: 'Sync New Orders', run: syncNewOrders },
        { key: 'orders-all', label: 'Sync All Orders', run: syncAllOrders },
        { key: 'products', label: 'Sync Products', run: syncProducts },
        { key: 'inventory', label: 'Sync Inventory', run: syncInventory },
        { key: 'marketplace-offers', label: 'Sync Marketplace Offers', run: syncMarketplaceOffers },
    ];

    const handleSync = async (action: typeof syncActions[number]) => {
        setActiveSync(action.key);
        try {
            const { jobId } = await action.run(connectionId);
            addJob({
                id: jobId,
                connector: 'product_flow',
                label: `${connection?.name ?? 'ProductFlow'} · ${action.label.replace(/^Sync /, '')}`,
            });
            await queryClient.invalidateQueries({ queryKey: ACTIVE_IMPORT_JOBS_QUERY_KEY });
            addToast(`${action.label} started`, 'success');
        } catch {
            addToast(`${action.label} failed`, 'error');
        } finally {
            setActiveSync(null);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await removeConnection(connectionId);
            addToast('Connection deleted', 'success');
            onClose();
        } catch {
            addToast('Failed to delete connection', 'error');
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Stack
                direction="row"
                position="sticky"
                top={0}
                p={4}
                zIndex={1}
                bgcolor="background.paper"
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    {connection && (
                        <Avatar
                            variant="rounded"
                            src={connection.connector.image}
                            alt={connection.connector.type}
                            sx={{ width: 36, height: 36, border: 1, borderColor: 'divider' }}
                            slotProps={{ img: { style: { objectFit: 'contain' } } }}
                        />
                    )}
                    <Typography variant="h6">{connection?.name ?? 'Connection Settings'}</Typography>
                </Stack>
                {onClose && (
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                )}
            </Stack>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 4, pb: 4 }}>

                {/* Import Settings */}
                <Paper>
                    <DialogHeader>
                        <Typography variant="h6">Import Settings</Typography>
                    </DialogHeader>
                    <Box sx={{ p: 3 }}>
                        {isLoading ? (
                            <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Stack spacing={3}>
                                <Box>
                                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                                        <Typography variant="subtitle2">Marketplaces</Typography>
                                        {isLoadingMarketplaces && (
                                            <CircularProgress size={14} />
                                        )}
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        Choose which marketplaces to import from. Select all marketplaces to import without restriction.
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Chip
                                            label="All marketplaces"
                                            clickable
                                            disabled={isUpdating}
                                            color={allMarketplacesSelected ? 'primary' : 'default'}
                                            variant={allMarketplacesSelected ? 'filled' : 'outlined'}
                                            icon={allMarketplacesSelected ? <Check fontSize="small" /> : undefined}
                                            onClick={() => handleToggle(ALL_MARKETPLACES)}
                                        />
                                        {availableMarketplaces.map(marketplace => {
                                            const selected = !allMarketplacesSelected && selectedMarketplaces.includes(marketplace.machine_name);
                                            return (
                                                <Chip
                                                    key={marketplace.machine_name}
                                                    label={marketplace.name}
                                                    clickable
                                                    disabled={isUpdating}
                                                    color={selected ? 'primary' : 'default'}
                                                    variant={selected ? 'filled' : 'outlined'}
                                                    icon={selected ? <Check fontSize="small" /> : undefined}
                                                    onClick={() => handleToggle(marketplace.machine_name)}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2">Match products by</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Which field is used to link imported ProductFlow data to your Juststock products.
                                    </Typography>
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={matchField}
                                        onChange={(_, value: ProductMatchField | null) => value && setMatchField(value)}
                                        disabled={isUpdating}
                                    >
                                        <ToggleButton value="ean">EAN</ToggleButton>
                                        <ToggleButton value="sku">SKU</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                <Box>
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                        disabled={isLoading || isUpdating || !isDirty}
                                        startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : undefined}
                                    >
                                        {isUpdating ? 'Saving…' : 'Save changes'}
                                    </Button>
                                    {!isDirty && !isUpdating && (
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                            All changes saved
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        )}
                    </Box>
                </Paper>

                {/* Sync Actions — impersonators only */}
                {canImpersonate && (
                    <Paper>
                        <DialogHeader>
                            <Typography variant="h6">Sync</Typography>
                        </DialogHeader>
                        <Box sx={{ p: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Syncs are queued in the background. Track progress below.
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {syncActions.map(action => (
                                    <Button
                                        key={action.key}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleSync(action)}
                                        disabled={activeSync !== null}
                                        startIcon={activeSync === action.key
                                            ? <CircularProgress size={14} color="inherit" />
                                            : undefined}
                                    >
                                        {activeSync === action.key ? 'Starting…' : action.label}
                                    </Button>
                                ))}
                            </Stack>
                        </Box>
                    </Paper>
                )}

                <Paper>
                    <DialogHeader>
                        <Typography variant="h6">Active Imports</Typography>
                    </DialogHeader>
                    <ConnectionActiveImports connectionId={connectionId} />
                </Paper>

                {/* Danger Zone */}
                <Paper>
                    <DialogHeader>
                        <Typography variant="h6" color="error.main">Danger Zone</Typography>
                    </DialogHeader>
                    <Box sx={{ p: 3 }}>
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                Deleting this connection will remove all associated listings and data. This action cannot be undone.
                            </Typography>
                            <Box>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => setDeleteDialogOpen(true)}
                                >
                                    Delete Connection
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                </Paper>

            </Box>

            {/* Delete Confirmation */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
                        p: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6">Confirm Delete</Typography>
                    <IconButton onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 3 }}>
                    <Typography>
                        Are you sure you want to delete <strong>{connection?.name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${APP_CONFIG.ui.theme.colors.border}` }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ProductFlowConnectionSettings;
